import React from 'react';
import OrangeIcon from './OrangeIcon';
import { ArrowRight, PlayCircle } from 'lucide-react';
import { BRAND, HERO_INTRO, HERO_FEATURES, HERO_STRIP } from '../mock';

const scrollTo = (id) => (e) => {
  e.preventDefault();
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

const MODULES = [
  { key: 'commander', title: 'COMMANDER',        sub: 'Hand Held',        img: '/commander_mod.png', anim: 'slideInFromLeft',   float: 'moduleFloat1', flex: 1   },
  { key: 'helmet',    title: 'HELMET MOUNTABLE',  sub: 'Helmet & Uniform', img: '/helmet_mod.png',    anim: 'slideInFromBottom', float: 'moduleFloat2', flex: 1.3 },
  { key: 'booster',   title: 'PORTABLE BOOSTER',  sub: 'Pocket Carry',     img: '/booster_mod.png',   anim: 'slideInFromRight',  float: 'moduleFloat3', flex: 1   },
];

const HeroSection = () => {
  return (
    <section id="home" className="relative overflow-hidden bg-[#0A1628]">
      {/* Dark navy background gradient */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background:
          'radial-gradient(70% 50% at 30% 20%, rgba(245,144,31,0.08) 0%, transparent 55%),' +
          'radial-gradient(50% 40% at 80% 5%, rgba(245,144,31,0.05) 0%, transparent 50%),' +
          'linear-gradient(180deg, #0d1e35 0%, #0A1628 40%, #0A1628 100%)'
      }} />
      <div className="hero-spotlight" />

      <div className="relative">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 pb-16 md:pb-20">

          {/* ── Two-column hero: items-center vertically aligns left content with right card */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 pt-6 md:pt-10 items-center">

            {/* LEFT: HERTZX identity */}
            <div className="animate-fadeInLeft">
              <h1 className="font-display font-black leading-[0.95] text-white">
                <span className="block text-6xl md:text-7xl xl:text-8xl tracking-tight">
                  {BRAND.product}<sup className="text-2xl md:text-3xl align-super orange-accent">TM</sup>
                </span>
                <span className="block text-3xl md:text-4xl xl:text-5xl mt-2">INTEGRATED</span>
                <span className="block text-4xl md:text-5xl xl:text-6xl orange-accent">HANDS-FREE</span>
                <span className="block text-3xl md:text-4xl xl:text-5xl">COMMUNICATION SYSTEM</span>
              </h1>

              <div className="mt-5 flex items-center gap-3 text-white/85 text-sm font-semibold tracking-[0.14em]">
                <span>FULL DUPLEX</span>
                <span className="text-[#F5901F]">|</span>
                <span>HANDS-FREE</span>
                <span className="text-[#F5901F]">|</span>
                <span>MISSION READY</span>
              </div>

              <p className="mt-5 text-white/80 leading-relaxed max-w-md">{HERO_INTRO}</p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#contact"
                  onClick={scrollTo('#contact')}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#F5901F] text-black font-bold hover:bg-[#ffa337] transition-colors shadow-lg shadow-[#F5901F]/25"
                >
                  Request a Demo <ArrowRight size={16} />
                </a>
                <a
                  href="#modules"
                  onClick={scrollTo('#modules')}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-[#F5901F]/70 text-[#F5901F] font-bold hover:bg-[#F5901F]/10 transition-colors"
                >
                  <PlayCircle size={16} /> Explore Product
                </a>
              </div>
            </div>

            {/* RIGHT: product showcase card */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden dark-card p-4 md:p-6">
                {/* Radial glow backdrop */}
                <div className="absolute inset-0 pointer-events-none" style={{
                  background: 'radial-gradient(60% 55% at 50% 55%, rgba(245,144,31,0.22) 0%, transparent 65%)'
                }} />
                {/* Rotating conic accent ring */}
                <div className="absolute -inset-20 pointer-events-none opacity-30" style={{
                  background: 'conic-gradient(from 0deg, rgba(245,144,31,0.35), transparent 30%, transparent 70%, rgba(245,144,31,0.35))',
                  animation: 'conicSpin 18s linear infinite',
                  filter: 'blur(30px)',
                }} />

                {/* 3-module row — flex so each image fills its column proportionally */}
                <div className="relative flex items-end justify-between gap-2 md:gap-3 pt-6 md:pt-8">
                  {MODULES.map((m, i) => (
                    <div
                      key={m.key}
                      className="relative group flex flex-col items-center"
                      style={{
                        flex: m.flex,
                        zIndex: i === 1 ? 3 : 1,
                        animation: `${m.anim} 1s cubic-bezier(0.22, 1, 0.36, 1) both`,
                        animationDelay: `${0.2 + i * 0.18}s`,
                      }}
                    >
                      <div
                        className="relative w-full"
                        style={{
                          animation: `${m.float} 6s ease-in-out infinite`,
                          animationDelay: `${0.9 + i * 0.35}s`,
                        }}
                      >
                        {/* Signal rings on center helmet */}
                        {i === 1 && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <span
                              className="absolute w-[120%] h-[120%] rounded-full border-2 border-[#F5901F]/40"
                              style={{ animation: 'signalRing 3.2s ease-out infinite' }}
                            />
                            <span
                              className="absolute w-[120%] h-[120%] rounded-full border-2 border-[#F5901F]/40"
                              style={{ animation: 'signalRing 3.2s ease-out infinite', animationDelay: '1.6s' }}
                            />
                          </div>
                        )}
                        <img
                          src={m.img}
                          alt={m.title}
                          className="w-full h-auto object-contain transition-transform duration-500 group-hover:scale-[1.06]"
                          style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.75))' }}
                          loading="eager"
                        />
                      </div>
                      <div className="mt-3 pb-3 text-center">
                        <div className="text-[#F5901F] text-xs font-extrabold tracking-[0.16em] leading-tight">{m.title}</div>
                        <div className="text-white/70 text-xs mt-0.5">{m.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bottom glow accent */}
                <div className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none" style={{
                  background: 'radial-gradient(60% 100% at 50% 100%, rgba(245,144,31,0.35), transparent 70%)'
                }} />
              </div>
            </div>
          </div>

          {/* Stat strip */}
          <div className="mt-10 rounded-xl border border-white/10 bg-[#101F36] px-4 md:px-6 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {HERO_STRIP.map((s, i) => (
                <div key={s.title + i} className="flex items-center gap-3 animate-fadeInUp" style={{ animationDelay: `${0.1 + i * 0.08}s` }}>
                  <OrangeIcon name={s.icon} size={38} iconSize={18} />
                  <div>
                    <div className="text-white font-bold text-sm leading-none">{s.title}</div>
                    <div className="text-white/60 text-[10px] tracking-wider mt-1">{s.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Features marquee strip */}
          <div
            className="relative overflow-hidden mt-6 border-t border-white/10 pt-4 pb-2"
            style={{ maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)' }}
          >
            <div className="flex gap-0 whitespace-nowrap" style={{ animation: 'marqueeScroll 28s linear infinite' }}>
              {[...HERO_FEATURES, ...HERO_FEATURES].map((f, i) => (
                <div key={i} className="inline-flex items-center gap-2.5 px-6 shrink-0">
                  <OrangeIcon name={f.icon} size={30} iconSize={14} />
                  <div>
                    <div className="text-[#F5901F] font-bold text-xs tracking-wide leading-none">{f.title}</div>
                    <div className="text-white/60 text-[10px] mt-0.5">{f.desc}</div>
                  </div>
                  <span className="ml-6 text-white/20 text-lg">|</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;
